<template>
  <div>
    <div v-if="state.isLoading">
      <b-spinner></b-spinner>
    </div>
    <div v-if="state.fetchError">
      <p>Error fetching interactions :(</p>
    </div>
    <div v-if="!state.isLoading && !state.fetchError">
      <b-row>
        <b-col md="6" lg="8">
          <b-form @submit="handleSearchForm">
            <b-form-group
              label="Sentiment Filter"
              label-size="sm"
              label-cols-md="4"
              label-cols-lg="3"
              label-cols-xl="2"
            >
              <b-form-select
                size="sm"
                :options="interactionTagOptions"
                v-model="tagFilter"
                @change="$refs.interactionsTable.refresh()"
              ></b-form-select>
            </b-form-group>
            <b-form-group
              label="User Search"
              label-size="sm"
              label-cols-md="4"
              label-cols-lg="3"
              label-cols-xl="2"
            >
              <b-input-group>
                <b-form-input
                  size="sm"
                  v-model="searchText"
                  trim
                ></b-form-input>
                <b-input-group-append>
                  <b-button
                    @click="$refs.interactionsTable.refresh()"
                    variant="secondary"
                    size="sm"
                    >go</b-button
                  >
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-form>
        </b-col>
        <b-col md="6" lg="4" align-self="end">
          <b-pagination
            v-model="currentPage"
            align="right"
            size="sm"
            :total-rows="interactionsTotal"
            :per-page="perPage"
            :aria-controls="tableId"
          ></b-pagination>
        </b-col>
      </b-row>
      <b-table
        :id="tableId"
        class="vertical-align-cols"
        small
        :items="fetch"
        :fields="dataTableFields"
        :per-page="perPage"
        :current-page="currentPage"
        ref="interactionsTable"
      >
        <template v-slot:cell(details)="data">
          <a @click="data.toggleDetails"
            ><b-icon-circle
              class="icon-link"
              v-if="!data.detailsShowing"
            ></b-icon-circle
            ><b-icon-circle-fill class="icon-link" v-else></b-icon-circle-fill
          ></a>
        </template>

        <template v-slot:cell(mentor)="data">
          <a
            target="_blank"
            :href="`${userProfileUrl}${data.item.mentor.id}`"
            >{{ data.item.mentor.name }}</a
          >
        </template>

        <template v-slot:cell(actions)="data">
          <a
            :href="`${messageRootUrl}${data.item.mentor.id}`"
            class="btn btn-primary btn-sm red action-button"
          >
            <b-icon-envelope-fill class="action-icon"></b-icon-envelope-fill>
            email mentor
          </a>
          <a
            :href="`${viewInteractionUrl}${data.item.id}`"
            class="btn btn-primary btn-sm red action-button"
          >
            <b-icon-file-earmark-text
              class="action-icon"
            ></b-icon-file-earmark-text>
            view interaction
          </a>
        </template>
        <template v-slot:row-details="row">
          <div class="text-left pl-2">
            <p>
              <strong>Sent to: </strong>
              <template v-for="(student, idx) in row.item.students">
                <span v-if="idx > 0" :key="student.id">, </span>
                <a
                  :href="`${userProfileUrl}${student.id}`"
                  target="_blank"
                  :key="student.id"
                  >{{ `${student.firstName} ${student.lastName}` }}</a
                >
              </template>
            </p>
            <p>
              <strong>Interaction type:</strong> {{ row.item.type }}<br />
              <strong>Topics:</strong> {{ row.item.topics }}
            </p>
          </div>
        </template>
      </b-table>
    </div>
  </div>
</template>

<script>
import MentorApplicationApi from "@/components/lib/MentorApplicationApi";
import CreateIdFactory from "@/components/lib/IdGeneratorFactory";
import {
  BButton,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BIconCircle,
  BIconCircleFill,
  BIconEnvelopeFill,
  BIconFileEarmarkText,
  BInputGroup,
  BInputGroupAppend,
  BPagination,
  BRow,
  BTable
} from "bootstrap-vue";
const idFactory = CreateIdFactory("interactions_table_{count}");

export default {
  name: "InteractionsGrid",
  components: {
    BButton,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BIconCircle,
    BIconCircleFill,
    BIconEnvelopeFill,
    BIconFileEarmarkText,
    BInputGroup,
    BInputGroupAppend,
    BPagination,
    BRow,
    BTable
  },
  data() {
    return {
      tableId: idFactory(),
      state: {
        isLoading: false,
        fetchError: false
      },
      searchText: "",
      tagFilter: null,
      interactionsTotal: 0,
      interactions: [],
      api: new MentorApplicationApi(this.apiRootUrl),
      currentPage: 1,
      perPage: 5,
      dataTableFields: [
        {
          key: "details",
          label: ""
        },
        {
          key: "mentor",
          class: "text-left"
        },
        {
          key: "startTime",
          label: "Interaction Date"
        },
        {
          key: "tag",
          label: "Sentiment"
        },
        "actions"
      ]
    };
  },
  computed: {
    interactionTagOptions() {
      let result = [
        {
          value: null,
          text: "include all sentiments"
        }
      ];
      result.push(
        ...this.interactionTags.map(x => {
          return { value: x.id + "", text: x.label };
        })
      );
      return result;
    },
    dataTable() {
      if (!Array.isArray(this.interactions)) {
        return [];
      }
      return this.interactions.map(x => {
        return {
          id: x.id,
          mentor: {
            name: `${x.mentor.firstName} ${x.mentor.lastName}`,
            id: x.mentor.id
          },
          students: x.students,
          startTime: new Date(x.startTime).toLocaleString([], {
            year: "numeric",
            month: "numeric",
            day: "numeric"
          }),
          type: x.type.label,
          tag: x.tag.label,
          topics: x.topics.map(topic => topic.label).join(", ")
        };
      });
    },
    tagFilterLabel() {
      return this.interactionTagOptions.find(x => x.value === this.tagFilter)
        .text;
    }
  },
  created() {
    if (this.currentPage !== this.initialCurrentPage) {
      this.currentPage = this.initialCurrentPage;
    }
    if (this.perPage !== this.initialPerPage) {
      this.perPage = this.initialPerPage;
    }
    // set the table to expect values so that it makes an initial request
    this.interactionsTotal = this.perPage;
  },
  methods: {
    handleSearchForm(e) {
      e.preventDefault();
      this.$refs.interactionsTable.refresh();
    },
    fetch(context, callback) {
      let that = this;
      let requestData = {
        pageSize: context.perPage,
        page: context.currentPage
      };
      if (this.tagFilter) {
        requestData.sentiment = this.tagFilterLabel;
      }
      if ((this.searchText?.trim()?.length || 0) > 0) {
        requestData.searchString = this.searchText;
      }
      this.api
        .getInteractions(requestData)
        .then(
          response => {
            that.state.fetchError = false;
            that.state.isLoading = false;
            that.interactions = response.data.interactions;
            that.interactionsTotal = response.data.interactionsTotal;
            callback(that.dataTable);
          },
          error => {
            that.state.fetchError = true;
            that.state.isLoading = false;
            alert(
              `Unable to fetch interactions: ${
                null === error?.response?.data?.error
                  ? "unknown error"
                  : error.response.data.error
              }`
            );
          }
        )
        .catch(() => {
          that.state.fetchError = true;
          that.state.isLoading = false;
          alert("Unknown error fetching interactions");
        });
    }
  },
  props: {
    initialInteractions: {
      type: Object,
      default: () => {}
    },
    apiRootUrl: {
      type: String,
      required: true
    },
    messageRootUrl: {
      type: String,
      required: true
    },
    userProfileUrl: {
      type: String,
      required: true
    },
    viewInteractionUrl: {
      type: String,
      required: true
    },
    interactionTags: {
      type: Array,
      required: true,
      validator: value => Array.isArray(value) && value.length > 0
    },
    initialCurrentPage: {
      type: Number,
      default: 1
    },
    initialPerPage: {
      type: Number,
      default: 5
    }
  }
};
</script>
<style lang="scss"></style>
